/**
 * One Data Lake write per dialog (S8 Task 8.10): the S3 single-request latch (`useSingleStorageRequest` — a second
 * submission while one is pending is refused, so a double click can never dispatch twice) plus the IPC answer
 * unwrapped into the typed error. Closing the dialog (or unmounting it) cancels the in-flight request exactly once.
 */
import { useCallback } from 'react';
import type { StorageIpcResult, StorageOperationError } from '../../../../types/azureStorage';
import { useSingleStorageRequest } from '../properties/hooks/useSingleStorageRequest';
import { toStorageOperationError } from '../services/azureStorageApi';

export type HnsMutationOutcome<T> = { current: boolean; started: boolean; value?: T; error?: StorageOperationError };

export function useHnsPathMutation() {
  const single = useSingleStorageRequest();
  const runSingle = single.run;
  const run = useCallback(async <T,>(invoke: (requestId: string) => Promise<StorageIpcResult<T>>): Promise<HnsMutationOutcome<T>> => {
    const outcome = await runSingle(invoke);
    if (!outcome.started) return { current: false, started: false };
    if (outcome.error !== undefined || !outcome.value) return { current: outcome.current, started: true, error: toStorageOperationError(outcome.error) };
    const answer = outcome.value;
    return answer.ok === true ? { current: outcome.current, started: true, value: answer.value } : { current: outcome.current, started: true, error: answer.error };
  }, [runSingle]);
  return { pending: single.pending, run, cancel: single.cancel };
}
